import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { suppliersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Ship, LogOut, LayoutDashboard, Package, Users,
  Plus, Search, ChevronRight, Loader2, Trash2, Edit2,
  Building2, Globe, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

function Sidebar({ onLogout }) {
  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
          <Ship className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
      </Link>
      
      <nav className="flex-1 space-y-1">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <LayoutDashboard className="w-5 h-5" />
          Dashboard
        </Link>
        <Link
          to="/dashboard/shipments"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <Package className="w-5 h-5" />
          Shipments
        </Link>
        <Link
          to="/dashboard/suppliers"
          className="flex items-center gap-3 px-4 py-3 rounded-md bg-importdocs-primary text-white"
        >
          <Users className="w-5 h-5" />
          Suppliers
        </Link>
      </nav>
      
      <Button 
        variant="ghost" 
        className="justify-start text-importdocs-text-secondary hover:text-importdocs-danger"
        onClick={onLogout}
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </aside>
  );
}

function SupplierCard({ supplier, onEdit, onDelete }) {
  return (
    <Card className="feature-card border-slate-200 hover:border-importdocs-primary/30" data-testid={`supplier-card-${supplier.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-importdocs-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-importdocs-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-importdocs-text">{supplier.name}</h3>
              <p className="text-sm text-importdocs-text-muted flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {supplier.country}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => onEdit(supplier)}
              data-testid={`edit-supplier-${supplier.id}`}
            >
              <Edit2 className="w-4 h-4 text-importdocs-text-muted" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => onDelete(supplier)}
              data-testid={`delete-supplier-${supplier.id}`}
            >
              <Trash2 className="w-4 h-4 text-importdocs-text-muted hover:text-importdocs-danger" />
            </Button>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          {supplier.contact_name && (
            <div className="flex items-center gap-2 text-importdocs-text-secondary">
              <Users className="w-4 h-4" />
              {supplier.contact_name}
            </div>
          )}
          {supplier.email && (
            <div className="flex items-center gap-2 text-importdocs-text-secondary">
              <Mail className="w-4 h-4" />
              {supplier.email}
            </div>
          )}
          {supplier.phone && (
            <div className="flex items-center gap-2 text-importdocs-text-secondary">
              <Phone className="w-4 h-4" />
              {supplier.phone}
            </div>
          )}
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-importdocs-text-muted">Shipments</p>
            <p className="text-lg font-bold text-importdocs-text">{supplier.shipment_count}</p>
          </div>
          <div>
            <p className="text-xs text-importdocs-text-muted">Total Value</p>
            <p className="text-lg font-bold text-importdocs-text font-mono">{formatCurrency(supplier.total_value)}</p>
          </div>
        </div>
        
        <Link to={`/dashboard/suppliers/${supplier.id}`}>
          <Button variant="outline" className="w-full mt-4" data-testid={`view-supplier-${supplier.id}`}>
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function SupplierFormDialog({ open, onOpenChange, supplier, onSave }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    notes: '',
  });

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        country: supplier.country || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
      });
    } else {
      setFormData({
        name: '',
        contact_name: '',
        email: '',
        phone: '',
        country: '',
        address: '',
        notes: '',
      });
    }
  }, [supplier, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.country) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      if (supplier) {
        await suppliersAPI.update(supplier.id, formData);
        toast.success('Supplier updated successfully');
      } else {
        await suppliersAPI.create(formData);
        toast.success('Supplier created successfully');
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{supplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          <DialogDescription>
            {supplier ? 'Update supplier information' : 'Add a new supplier to your list'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Company Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., ABC Manufacturing Co."
              data-testid="supplier-name-input"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              placeholder="e.g., China"
              data-testid="supplier-country-input"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="supplier@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 Manufacturing District"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-importdocs-primary hover:bg-importdocs-primary-dark"
              disabled={loading}
              data-testid="save-supplier-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (supplier ? 'Update' : 'Add Supplier')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SuppliersPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.getAll();
      setSuppliers(res.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await suppliersAPI.delete(deleteTarget.id);
      setSuppliers(prev => prev.filter(s => s.id !== deleteTarget.id));
      toast.success('Supplier deleted');
    } catch (error) {
      toast.error('Failed to delete supplier');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-importdocs-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-importdocs-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-importdocs-bg flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="font-heading font-bold text-xl text-importdocs-text">Suppliers</h1>
            <Button 
              className="bg-importdocs-primary hover:bg-importdocs-primary-dark" 
              onClick={handleAdd}
              data-testid="add-supplier-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Supplier
            </Button>
          </div>
        </header>
        
        <main className="flex-1 p-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-importdocs-text-muted" />
                <Input 
                  placeholder="Search suppliers by name or country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-200"
                  data-testid="search-suppliers-input"
                />
              </div>
            </CardHeader>
            <CardContent>
              {filteredSuppliers.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-importdocs-text-muted mx-auto mb-4" />
                  <p className="text-importdocs-text-secondary">
                    {searchTerm ? 'No suppliers match your search' : 'No suppliers yet'}
                  </p>
                  {!searchTerm && (
                    <Button variant="outline" className="mt-4" onClick={handleAdd}>
                      Add your first supplier
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredSuppliers.map((supplier) => (
                    <SupplierCard 
                      key={supplier.id} 
                      supplier={supplier}
                      onEdit={handleEdit}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
      
      <SupplierFormDialog 
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplier={editingSupplier}
        onSave={loadSuppliers}
      />
      
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This will not delete associated shipments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-importdocs-danger hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
